/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import * as d3 from 'd3';
import {
  calculateThetaFromPressTemp,
  pressToPascal,
  tempToKelvin,
} from './bijvoetUtils';
import type { SoundingDiagramType } from '../../api/edr-api/types';
import {
  KELVIN,
  minEmagramTemperature,
  minTemperature,
  REFERENCE_PRESSURE,
} from '../Soundings/constants';
import type { ScalesType } from '../Soundings/types';

const CP = 1.03e3;
const RD = 287.0;
const Po = 1000.0;

const dryAdiabaticLapserate = (
  theta: number,
  pressure: number,
  temperature: number,
  dp: number,
): [number, number] => {
  const Tt = (theta + KELVIN) * (Po / pressure) ** (-RD / CP) - KELVIN;
  const dt = Tt - temperature;

  return [dp, dt];
};

interface DrawDryAdiabatsProps {
  staticPressureArray: number[];
  height: number;
  xScale: ScalesType;
  yScale: ScalesType;
  dp: number;
  svg: d3.Selection<SVGElement, unknown, HTMLElement, unknown>;
  diagramType: SoundingDiagramType;
  d3GraphId: string;
}

export const drawDryAdiabats = ({
  staticPressureArray,
  height,
  xScale,
  yScale,
  svg,
  dp,
  diagramType,
  d3GraphId,
}: DrawDryAdiabatsProps): void => {
  const minT =
    diagramType === 'EMAGRAM' ? minEmagramTemperature : minTemperature;

  for (let Tbase = minT; Tbase < 240; Tbase += 10) {
    let Pnew = staticPressureArray[0];
    let Tnew = Tbase;
    const Px: [number, number][] = [];

    for (const pressure of staticPressureArray) {
      const DPDT = dryAdiabaticLapserate(Tbase, Pnew, Tnew, dp);

      Tnew += DPDT[1];
      Pnew += DPDT[0];

      Px.push([xScale(Tnew), yScale(pressure)]);
    }

    if (diagramType === 'SKEWT') {
      // Skew/shear transformation for skew-t
      for (const PxElement of Px) {
        PxElement[0] = PxElement[0] + height - PxElement[1];
      }
    }

    const lineGenerator = d3.line();
    const daPathString = lineGenerator(Px);

    svg
      .append('path')
      .attr('d', daPathString)
      .style('fill', 'none')
      .style('stroke-width', 1)
      .style('stroke', 'green')
      .style('opacity', 0.3)
      .attr('clip-path', `url(#clip-${d3GraphId}-${diagramType}d3)`);
  }
};

interface DrawDryAdiabatsBijvoetProps {
  staticPressureArray: number[];
  xScale: ScalesType;
  yScale: ScalesType;
  svg: d3.Selection<SVGElement, unknown, HTMLElement, unknown>;
  d3GraphId: string;
}

const tempPresDryAiabat = (
  pressure: number,
  pres0: number,
  temp0: number,
): number => temp0 * (pressure / pres0) ** (2.0 / 7.0);

export const drawDryAdiabatsBijvoet = ({
  staticPressureArray,
  xScale,
  yScale,
  svg,
  d3GraphId,
}: DrawDryAdiabatsBijvoetProps): void => {
  const lineGenerator = d3.line<[number, number]>();

  for (let Tbase = -20; Tbase < 40; Tbase += 5) {
    const Px: [number, number][] = [];

    const tempKelvin = tempToKelvin(Tbase);
    const tempDry = calculateThetaFromPressTemp(
      REFERENCE_PRESSURE,
      tempKelvin,
      1000,
    );

    for (const pressure of staticPressureArray) {
      const pres = pressToPascal(pressure); // Pressure in Pa
      const tempPresDry = tempPresDryAiabat(pres, 1000, tempDry);
      const theta = calculateThetaFromPressTemp(pres, tempPresDry);
      const Ppx = yScale(pressure);
      const Tpx = xScale(theta);
      Px.push([Tpx, Ppx]);
    }

    const daPathString = lineGenerator(Px);
    const strID = `${Tbase}-dryadiabat`;

    svg
      .append('path')
      .attr('id', strID)
      .attr('d', daPathString)
      .style('fill', 'none')
      .style('stroke-width', 1)
      .style('stroke', 'green')
      .style('opacity', 0.3)
      .attr('clip-path', `url(#clip-${d3GraphId}-BIJVOETd3)`);
  }
};
